import React, { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import SimpleTable from "../../components/tables/simpleTable";
import LaddaButton from "../../components/buttons/laddaButton";
import { icons } from "../../constants";
import ModalComponent from "../../components/modals";
import VerificationModal from "../../components/modals/verificationModal";
import SimpleSelect from "../../components/selects/simpleSelect";
import FormTextField from "../../components/textField/formTextField";
import ExcelUploadField from "../../components/excelUploadField";
import {
  downloadBiomedicalEquipmentAdverseEventFile,
  downloadBiomedicalEquipmentAdverseEventFileByPath,
  postUploadAdverseEventFile
} from "../../services/apiService";
import { downloadBlob } from "../../utils/downloadUtils";

const validationSchema = yup.object().shape({
  type_event_id: yup.string().required("El tipo de suceso es obligatorio"),
  gravity_level_id: yup.string().required("La gravedad es obligatoria"),
  amount: yup
    .number()
    .typeError("La cantidad debe ser un numero")
    .required("La cantidad es obligatoria")
    .positive("Debe ser mayor a 0")
    .integer("Debe ser un numero entero"),
  invima_radicado: yup.string().optional(),
  description: yup.string().optional()
});

const getOptionLabel = (options, value, fallback = "") => {
  if (!Array.isArray(options)) return fallback;

  const match = options.find(
    (option) => option?.value != null && String(option.value) === String(value)
  );

  return match ? match.name : fallback;
};

const getEventFilePath = (row) => row?.filePath || row?.file_path || null;
const hasEventFile = (row) => String(getEventFilePath(row) || "").trim() !== "";
const getEventOriginalFileName = (row) =>
  row?.originalFileName ||
  row?.original_file_name ||
  row?.fileName ||
  row?.file_name ||
  "";

const getEventStoredFileName = (row) => {
  const filePath = getEventFilePath(row);
  if (!filePath) return "";

  const normalizedPath = String(filePath).split("?")[0].replace(/\\/g, "/");
  return normalizedPath.split("/").pop() || "";
};

const getEventVisibleFileName = (row) =>
  getEventOriginalFileName(row) || getEventStoredFileName(row);

const sanitizeFileNamePart = (value, fallback = "sin_serie") => {
  const normalizedValue = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return normalizedValue || fallback;
};

const getEventDownloadName = (row, equipmentSerialNumber) => {
  const fileNameSource = getEventVisibleFileName(row) || getEventFilePath(row);
  const extensionMatch = String(fileNameSource || "").match(/(\.[^./?]+)(?:\?.*)?$/);
  const extension = extensionMatch?.[1] || "";
  const serialNumber = sanitizeFileNamePart(
    equipmentSerialNumber || row?.serieName || row?.serie_name
  );

  return `sucesodeseguridad_${serialNumber}${extension}`;
};

const AdverseEventsTable = ({
  adverseEventsDataTable,
  setAdverseEventsDataTable,
  isViewer,
  typeEventOptions = [],
  gravityOptions = [],
  biomedicalEquipmentId,
  equipmentSerialNumber = "",
  userId,
  isPageBusy,
  startProtectedAction,
  endProtectedAction
}) => {
  const [open, setOpen] = useState(false);
  const actionRef = useRef("idle");
  const [isSavingEvent, setIsSavingEvent] = useState(false);

  useEffect(() => {
    if (open || actionRef.current !== "closing") return;
    actionRef.current = "idle";
    setIsSavingEvent(false);
    endProtectedAction?.();
  }, [open, endProtectedAction]);

  const handleEventSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (actionRef.current !== "idle" || isViewer || isPageBusy) return;
    if (startProtectedAction && !startProtectedAction("adverseEvent")) return;
    actionRef.current = "running";
    setIsSavingEvent(true);
    try {
      await handleSubmit(addOrEditEvent)(event);
    } finally {
      if (actionRef.current === "running") {
        actionRef.current = "idle";
        setIsSavingEvent(false);
        endProtectedAction?.();
      }
    }
  };
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openDeleteVerification, setOpenDeleteVerification] = useState(false);
  const [selectedEventToDelete, setSelectedEventToDelete] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [openAttachmentVerification, setOpenAttachmentVerification] =
    useState(false);
  const [allowFileReplacement, setAllowFileReplacement] = useState(true);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      type_event_id: "",
      gravity_level_id: "",
      amount: "",
      invima_radicado: "",
      description: ""
    }
  });

  useEffect(() => {
    if (selectedEvent) {
      setValue("type_event_id", selectedEvent.type_event_id);
      setValue("gravity_level_id", selectedEvent.gravity_level_id);
      setValue("amount", selectedEvent.amount);
      setValue("invima_radicado", selectedEvent.invima_radicado || "");
      setValue("description", selectedEvent.description || "");
      setSelectedFile(null);
      setAllowFileReplacement(!hasEventFile(selectedEvent));
      return;
    }

    reset();
    setSelectedFile(null);
    setAllowFileReplacement(true);
  }, [selectedEvent, reset, setValue]);

  const openModal = () => {
    if (actionRef.current !== "idle" || isPageBusy) return;
    reset();
    setOpen(true);
  };

  const closeModal = () => {
    if (actionRef.current !== "idle" || isPageBusy) return;
    finishClose();
  };

  const finishClose = () => {
    reset();
    setSelectedEvent(null);
    setOpen(false);
    setSelectedFile(null);
    setOpenAttachmentVerification(false);
    setAllowFileReplacement(true);
  };

  const handleFileChange = (event) => {
    setSelectedFile(event);
  };

  const handleRequestFileReplacement = () => {
    if (!hasEventFile(selectedEvent)) {
      setAllowFileReplacement(true);
      return;
    }

    setOpenAttachmentVerification(true);
  };

  const handleConfirmFileReplacement = () => {
    setAllowFileReplacement(true);
    setOpenAttachmentVerification(false);
  };

  const addOrEditEvent = async (data) => {
    const isEditing = selectedEvent != null;
    const selectedEventId = selectedEvent?.id;
    const selectedEventRowTableId = selectedEvent?.rowTableId;
    const nextRowTableId =
      selectedEventRowTableId ||
      `new-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    let filePath = getEventFilePath(selectedEvent);
    let originalFileName = getEventOriginalFileName(selectedEvent);

    if (selectedFile) {
      const formData = new FormData();
      formData.append("requestId", crypto.randomUUID());
      formData.append("userId", userId);
      formData.append("file", selectedFile);

      const eventId = isEditing ? selectedEventId : `temp-${nextRowTableId}`;

      try {
        const uploadResponse = await postUploadAdverseEventFile(
          biomedicalEquipmentId,
          eventId,
          formData
        );
        filePath =
          uploadResponse?.data?.path ||
          uploadResponse?.data?.filePath ||
          uploadResponse?.data?.file_path ||
          filePath;
        originalFileName =
          uploadResponse?.data?.originalFileName ||
          uploadResponse?.data?.original_file_name ||
          selectedFile.name;
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    }

    const formattedData = {
      ...data,
      id: isEditing ? selectedEventId : 0,
      rowTableId: nextRowTableId,
      type_event_id: parseInt(data.type_event_id, 10),
      gravity_level_id: parseInt(data.gravity_level_id, 10),
      invima_radicado: data.invima_radicado || null,
      description: data.description || null,
      filePath: filePath,
      file_path: filePath,
      originalFileName: originalFileName,
      original_file_name: originalFileName
    };

    if (isEditing) {
      setAdverseEventsDataTable((prevData) =>
        prevData.map((event) =>
          event === selectedEvent ||
          (selectedEventRowTableId &&
            event.rowTableId === selectedEventRowTableId) ||
          (selectedEventId > 0 && Number(event.id) === Number(selectedEventId))
            ? formattedData
            : event
        )
      );
    } else {
      setAdverseEventsDataTable((prevData) => [...prevData, formattedData]);
    }

    actionRef.current = "closing";
    finishClose();
  };

  const handleEditRow = (row) => {
    setSelectedEvent(row);
    openModal();
  };

  const handleConfirmDelete = () => {
    setAdverseEventsDataTable((prevData) =>
      prevData.filter((event) => event !== selectedEventToDelete)
    );
    setOpenDeleteVerification(false);
    setSelectedEventToDelete(null);
  };

  const downloadFile = async (row) => {
    const filePath = getEventFilePath(row);
    if (!filePath) {
      console.warn("El evento adverso no tiene archivo para descargar.");
      return;
    }

    try {
      const response =
        Number(row?.id) > 0
          ? await downloadBiomedicalEquipmentAdverseEventFile(row.id)
          : await downloadBiomedicalEquipmentAdverseEventFileByPath(filePath);

      const blob =
        response?.data instanceof Blob
          ? response.data
          : new Blob([response?.data]);

      downloadBlob(blob, getEventDownloadName(row, equipmentSerialNumber));
    } catch (error) {
      console.error("Error descargando archivo del evento adverso:", error);
    }
  };

  const currentAttachedFileName =
    selectedFile?.name || getEventVisibleFileName(selectedEvent);
  const shouldShowFileInput =
    !selectedEvent || !hasEventFile(selectedEvent) || allowFileReplacement;
  const adverseEventFileAccept = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
  const validateAdverseEventFile = (file) => {
    const fileName = String(file?.name || "").toLowerCase();
    const isValidFile =
      fileName.endsWith(".pdf") ||
      fileName.endsWith(".doc") ||
      fileName.endsWith(".docx") ||
      fileName.endsWith(".jpg") ||
      fileName.endsWith(".jpeg") ||
      fileName.endsWith(".png");

    return isValidFile
      ? ""
      : "El archivo debe ser PDF, Word o imagen (JPG, JPEG o PNG).";
  };

  const adverseEventsColumns = [
    {
      field: "type_event_id",
      header: "Tipo de Suceso",
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {getOptionLabel(
            typeEventOptions,
            rowData.type_event_id,
            rowData.type_event_id === 1 ? "Evento" : "Incidente"
          )}
        </p>
      )
    },
    {
      field: "gravity_level_id",
      header: "Gravedad",
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {getOptionLabel(
            gravityOptions,
            rowData.gravity_level_id,
            rowData.gravity_level_id === 1 ? "Serio" : "No Serio"
          )}
        </p>
      )
    },
    { field: "amount", header: "Cantidad" },
    { field: "invima_radicado", header: "INVIMA Radicado" },
    { field: "description", header: "Descripcion" },
    { field: "actions", header: "", columnType: "actionButtons" }
  ];

  return (
    <div>
      {isViewer ? null : (
        <div className="IHContainer j-end">
          <LaddaButton
            width={"40px"}
            fontSize={"12px"}
            height={"40px"}
            variant="contained"
            margin={"0 5px"}
            disabled={isSavingEvent || isPageBusy}
            onClick={() => {
              setSelectedEvent(null);
              openModal();
            }}
          >
            <img className="btn-icon" src={icons.plusIcon} alt="icon" />
          </LaddaButton>
        </div>
      )}

      <div className="mt-15">
        <SimpleTable
          data={adverseEventsDataTable}
          columns={adverseEventsColumns}
          buttons={[
            {
              type: "icon",
              icon: isViewer ? icons.eye : icons.editYellow,
              onClick: (row) => handleEditRow(row)
            },
            {
              type: "icon",
              icon: isViewer ? icons.eye : icons.downloadBlue,
              onClick: (row) => downloadFile(row),
              hideCondition: (row) => !hasEventFile(row)
            }
          ]}
        />
      </div>

      <ModalComponent
        open={open}
        handleClose={closeModal}
        title={selectedEvent ? "Editar Evento Adverso" : "Agregar Evento Adverso"}
        actions={<div className="HContainer j-center w-complete"></div>}
      >
        <form onSubmit={handleEventSubmit}>
          <fieldset disabled={isSavingEvent || isPageBusy}
            style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Tipo de Suceso:</p>
              <SimpleSelect
                name="type_event_id"
                control={control}
                options={typeEventOptions}
                label=""
                error={errors.type_event_id}
                margin={"0"}
                disabled={isViewer}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Gravedad:</p>
              <SimpleSelect
                name="gravity_level_id"
                control={control}
                options={gravityOptions}
                label=""
                error={errors.gravity_level_id}
                margin={"0"}
                disabled={isViewer}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">Cantidad:</p>
              <FormTextField
                name="amount"
                control={control}
                label=""
                error={errors.amount}
                margin={"0"}
                disabled={isViewer}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <p className="create-label ml-0">INVIMA Radicado:</p>
              <FormTextField
                name="invima_radicado"
                control={control}
                label=""
                error={errors.invima_radicado}
                margin={"0"}
                disabled={isViewer}
              />
            </Grid>
            <Grid item xs={12}>
              <p className="create-label ml-0">Descripcion:</p>
              <FormTextField
                name="description"
                control={control}
                label=""
                error={errors.description}
                margin={"0"}
                disabled={isViewer}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <p className="create-label ml-0">Archivo:</p>
              {selectedEvent && hasEventFile(selectedEvent) && !allowFileReplacement && (
                <p className="file-name">
                  Adjunto actual:{" "}
                  {currentAttachedFileName}
                </p>
              )}
              {!isViewer &&
                selectedEvent &&
                hasEventFile(selectedEvent) &&
                !allowFileReplacement && (
                  <div className="mt-10">
                    <LaddaButton
                      width={"180px"}
                      fontSize={"12px"}
                      height={"36px"}
                      variant="contained"
                      onClick={handleRequestFileReplacement}
                    >
                      Cambiar adjunto
                    </LaddaButton>
                  </div>
                )}
              {shouldShowFileInput && (
                <div style={{ marginTop: "8px" }}>
                  <ExcelUploadField
                    label="Adjunto del evento"
                    onFileChange={handleFileChange}
                    disabled={isViewer}
                    accept={adverseEventFileAccept}
                    buttonText="Seleccionar archivo"
                    emptyText="Sin archivo seleccionado"
                    helperText="Formatos permitidos: PDF, Word o imagen (JPG, JPEG, PNG)"
                    validateFile={validateAdverseEventFile}
                    selectedFileName={currentAttachedFileName}
                    allowClear={Boolean(selectedFile)}
                    hideLabel
                  />
                </div>
              )}
            </Grid>
          </Grid>

          {isViewer ? null : (
            <div className="HContainer j-center white-space">
              <LaddaButton
                type="submit"
                width={"160px"}
                fontSize={"12px"}
                height={"40px"}
                variant="contained"
                loading={isSavingEvent}
                disabled={isSavingEvent || isPageBusy}
              >
                {selectedEvent ? "Guardar Cambios" : "Agregar Evento"}
              </LaddaButton>
            </div>
          )}
          </fieldset>
        </form>
      </ModalComponent>

      <VerificationModal
        open={openDeleteVerification}
        handleClose={() => setOpenDeleteVerification(false)}
        alertText="Seguro desea eliminar este evento adverso?"
        onConfirm={handleConfirmDelete}
      />

      <VerificationModal
        open={openAttachmentVerification}
        handleClose={() => setOpenAttachmentVerification(false)}
        alertText="Seguro quieres editar este adjunto de suceso adverso?"
        onConfirm={handleConfirmFileReplacement}
      />
    </div>
  );
};

export default AdverseEventsTable;
